// src/components/AuthSwitch.tsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Google, ExploreOutlined, Share } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import {
  listenForAuthChanges,
  loginWithEmail,
  loginWithGoogle,
  registerWithEmail,
  sendResetEmail,
} from "../redux/authSlice";
import { useNavigate } from "react-router-dom";
import logo from "../assets/app/logo.png";

const RED  = "#B91C1C";
const GOLD = "#D97706";

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');

  @keyframes areluxeGradientShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .areluxe-watermark {
    position: fixed;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
    opacity: 0.05;
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    grid-template-rows: repeat(6, 1fr);
    transform: rotate(-12deg) scale(1.4);
  }
  .areluxe-watermark img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 14px;
  }

  .areluxe-logo-above {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    padding: 3px;
    background: linear-gradient(135deg, #111111, ${GOLD}, ${RED}, ${GOLD}, #111111);
    background-size: 260% 260%;
    animation: areluxeGradientShift 4s ease infinite;
    box-shadow: 0 6px 22px rgba(217,119,6,0.25), 0 2px 10px rgba(185,28,28,0.15);
    margin: 0 auto 18px;
  }
  .areluxe-logo-above-inner {
    width: 100%;
    height: 100%;
    border-radius: 15px;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .areluxe-logo-above-inner img {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  .explore-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 13px 0;
    margin-top: 22px;
    border-radius: 999px;
    background: #ffffff;
    color: #111111;
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.18em;
    border: none;
    cursor: pointer;
    z-index: 0;
    box-shadow: 0 4px 14px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05);
    transition: transform 0.18s ease, box-shadow 0.18s ease;
  }
  .explore-btn::before {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: 999px;
    background: linear-gradient(120deg, #111111, ${RED}, ${GOLD}, #111111, ${RED}, ${GOLD});
    background-size: 300% 300%;
    animation: areluxeGradientShift 3.5s ease infinite;
    z-index: -1;
  }
  .explore-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 26px rgba(217,119,6,0.22);
  }
  .explore-btn:active {
    transform: translateY(0);
  }

  .share-page-btn {
    position: fixed;
    top: 22px;
    right: 22px;
    z-index: 50;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(135deg, #111111, ${GOLD}, ${RED}, ${GOLD}, #111111);
    background-size: 260% 260%;
    animation: areluxeGradientShift 4s ease infinite;
    box-shadow: 0 6px 20px rgba(217,119,6,0.25), 0 2px 8px rgba(185,28,28,0.15);
    cursor: pointer;
    transition: transform 0.18s cubic-bezier(.34,1.56,.64,1);
  }
  .share-page-btn:hover {
    transform: translateY(-3px) scale(1.06);
  }
  .share-page-btn-inner {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #111111;
  }
  .share-page-tooltip {
    position: absolute;
    top: 54px;
    right: 0;
    background: #111111;
    color: #fff;
    font-size: 10.5px;
    font-weight: 700;
    font-family: 'Montserrat', sans-serif;
    padding: 5px 10px;
    border-radius: 8px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }
  .share-page-btn:hover .share-page-tooltip {
    opacity: 1;
  }
`;

// ── Reusable field sx ──
const fsx = (focused = "#ffffff") => ({
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    color: "#e8e8e8",
    "& fieldset":             { borderColor: "#2a2a2a" },
    "&:hover fieldset":       { borderColor: "#555555" },
    "&.Mui-focused fieldset": { borderColor: focused },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    letterSpacing: "0.05em",
    color: "#6b6b6b",
    "&.Mui-focused": { color: focused },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 11,
    letterSpacing: "0.05em",
    color: "#6b6b6b",
  },
});

const AuthSwitch: React.FC = () => {
  const [mode, setMode]                   = useState<"login" | "signup">("login");
  const [email, setEmail]                 = useState("");
  const [password, setPassword]           = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword]   = useState(false);
  const [error, setError]                 = useState("");

  // ── Inline forgot-password state ──
  const [showReset, setShowReset]         = useState(false);
  const [resetEmail, setResetEmail]       = useState("");
  const [resetStatus, setResetStatus]     = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [resetError, setResetError]       = useState("");
  const [shareCopied, setShareCopied]     = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, user, authLoaded } = useSelector((s: RootState) => s.auth);

  useEffect(() => { if (authLoaded && user) navigate("/"); }, [authLoaded, user, navigate]);
  useEffect(() => { dispatch(listenForAuthChanges()); }, [dispatch]);

  const handleToggle = (newMode: "login" | "signup") => {
    setMode(newMode); setError("");
    setPassword(""); setConfirmPassword("");
    setShowReset(false); setResetStatus("idle"); setResetError("");
  };

  // ── open reset panel, pre-fill email if already typed ──
  const openReset = () => {
    setResetEmail(email);
    setResetStatus("idle");
    setResetError("");
    setShowReset(true);
  };

  const closeReset = () => {
    setShowReset(false);
    setResetStatus("idle");
    setResetError("");
    setResetEmail("");
  };

  // ── password validation ──
  const extractUsername = (mail: string) => mail.split("@")[0]?.toLowerCase() || "";
  const validatePassword = (pwd: string, em: string) => {
    const u = extractUsername(em);
    return {
      hasUpper:        /[A-Z]/.test(pwd),
      hasLower:        /[a-z]/.test(pwd),
      hasNumber:       /[0-9]/.test(pwd),
      hasSpecial:      /[^A-Za-z0-9]/.test(pwd),
      validLength:     pwd.length >= 6 && pwd.length <= 12,
      notEmail:        pwd.toLowerCase() !== em.toLowerCase(),
      notContainEmail: !pwd.toLowerCase().includes(em.toLowerCase()) && !pwd.toLowerCase().includes(u),
    };
  };

  const check         = validatePassword(password, email);
  const allValid      = Object.values(check).every(Boolean);
  const strengthScore = [check.hasUpper, check.hasLower, check.hasNumber, check.hasSpecial, check.validLength].filter(Boolean).length;
  const strengthLabel = ["Too Weak","Weak","Good","Strong","Very Strong"][strengthScore] || "Too Weak";

  const handleLogin = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    try { await dispatch(loginWithEmail({ email, password })).unwrap(); navigate("/"); }
    catch (err: any) { setError(err.message || "Login failed"); }
  };

  const handleRegister = async () => {
    setError("");
    if (!email || !password)          return setError("Please fill in all fields");
    if (password !== confirmPassword) return setError("Passwords do not match");
    if (!allValid)                    return setError("Password must meet all requirements.");
    try {
      const [firstName = "", lastName = ""] = email.split("@")[0].split(".");
      await dispatch(registerWithEmail({ email, password, firstName, lastName, phoneNumber: "", profilePicture: "", username: "" })).unwrap();
      navigate("/");
    } catch (err: any) { setError(err.message || "Something went wrong"); }
  };

  const handleGoogle = async () => {
    try { await dispatch(loginWithGoogle()).unwrap(); navigate("/"); }
    catch (err: any) { setError(err.message || "Google login failed"); }
  };

  // ── send password reset ──
  const handleSendReset = async () => {
    setResetError("");
    if (!resetEmail.trim()) return setResetError("Please enter your email address.");
    setResetStatus("sending");
    try {
      await dispatch(sendResetEmail(resetEmail.trim())).unwrap();
      setResetStatus("sent");
    } catch (err: any) {
      setResetStatus("error");
      setResetError(err.message || "Could not send reset email. Please try again.");
    }
  };

  // ── share the website ──
  const handleSharePage = async () => {
    const shareUrl = window.location.origin;
    const shareData = {
      text: "HEARTLAS",
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      }
    } catch {
      // user cancelled — no-op
    }
  };

  return (
    <Box sx={{ position:"relative", minHeight:"100vh", display:"flex", justifyContent:"center", alignItems:"center", background:"#ffffff", p:2, fontFamily:"'Montserrat', 'Cormorant Garamond', sans-serif", overflow:"hidden" }}>
      <style>{FONT_CSS}</style>

      {/* Tiny tiled logo watermark across the whole page */}
      <div className="areluxe-watermark">
        {Array.from({ length: 48 }).map((_, i) => (
          <img key={i} src={logo} alt="" />
        ))}
      </div>

      {/* 3D gradient share button — top right of the website */}
      <div className="share-page-btn" onClick={handleSharePage} title="Share Areluxe">
        <div className="share-page-btn-inner">
          <Share sx={{ fontSize: 18 }} />
        </div>
        <span className="share-page-tooltip">{shareCopied ? "Link copied!" : "Share"}</span>
      </div>

      <Box sx={{ position:"relative", zIndex:1, width:"100%", maxWidth:440 }}>

        {/* Logo above the card */}
        <div className="areluxe-logo-above">
          <div className="areluxe-logo-above-inner">
            <img src={logo} alt="Areluxe" />
          </div>
        </div>

        {/* ══ AUTH CARD — UI retained exactly ══ */}
        <Box sx={{ width:"100%", background:"linear-gradient(160deg, #6d0000 0%, #2e1c03 100%)", border:"1px solid #7a4a06", borderRadius:0, p:4 }}>

          {/* Brand */}
          <Typography sx={{ textAlign:"center", fontFamily:"'Cormorant Garamond', serif", fontWeight:300, fontSize:28, letterSpacing:"0.3em", color:"#ffffff", mb:4 }}>
          HEARTLAS
          </Typography>

          <AnimatePresence mode="wait">

            {/* ══════════════════════════════════════
                FORGOT PASSWORD — inline panel
            ══════════════════════════════════════ */}
            {showReset && (
              <motion.div
                key="reset"
                initial={{ opacity:0, x:24 }}
                animate={{ opacity:1, x:0 }}
                exit={{ opacity:0, x:24 }}
                transition={{ duration:0.22, ease:"easeOut" }}
              >
                {/* Back arrow */}
                <Box
                  onClick={closeReset}
                  sx={{ display:"flex", alignItems:"center", gap:1, cursor:"pointer", mb:3, width:"fit-content" }}
                >
                  <span style={{ color:"#555", fontSize:18, lineHeight:1, fontFamily:"'Montserrat', sans-serif" }}>←</span>
                  <span style={{ fontSize:10, letterSpacing:"0.14em", color:"#555", fontFamily:"'Montserrat', sans-serif", transition:"color 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                    onMouseLeave={e => e.currentTarget.style.color = "#555"}
                  >
                    BACK TO LOGIN
                  </span>
                </Box>

                <Typography sx={{ fontFamily:"'Cormorant Garamond', serif", fontWeight:400, fontSize:22, color:"#e8e8e8", letterSpacing:"0.04em", mb:0.8 }}>
                  Reset password
                </Typography>
                <Typography sx={{ fontSize:12, color:"#555", fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.02em", mb:3.5, lineHeight:1.75 }}>
                  Enter your email and we'll send a reset link instantly.
                </Typography>

                <AnimatePresence mode="wait">

                  {/* ── SENT confirmation ── */}
                  {resetStatus === "sent" ? (
                    <motion.div key="sent" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.2 }}>
                      <Box sx={{ py:2, textAlign:"center" }}>
                        <Box sx={{ width:46, height:46, border:"1px solid #2a2a2a", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", mx:"auto", mb:2 }}>
                          <span style={{ color:"#fff", fontSize:20 }}>✓</span>
                        </Box>
                        <Typography sx={{ fontSize:13, color:"#e8e8e8", fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.03em", mb:1 }}>
                          Check your inbox
                        </Typography>
                        <Typography sx={{ fontSize:11, color:"#555", fontFamily:"'Montserrat', sans-serif", lineHeight:1.75 }}>
                          We sent a link to{" "}
                          <span style={{ color:"#aaa" }}>{resetEmail}</span>.
                          {" "}It expires in 1 hour.
                        </Typography>
                        <Button
                          fullWidth onClick={closeReset}
                          sx={{ mt:3.5, py:1.5, borderRadius:0, background:"#ffffff", color:"#000000", fontFamily:"'Montserrat', sans-serif", fontSize:11, letterSpacing:"0.2em", "&:hover":{ background:"#e8e8e8" } }}
                        >
                          BACK TO LOGIN
                        </Button>
                      </Box>
                    </motion.div>

                  ) : (
                    /* ── Reset form ── */
                    <motion.div key="resetform" initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:0.18 }}>
                      <TextField
                        label="Email address"
                        fullWidth
                        value={resetEmail}
                        onChange={e => { setResetEmail(e.target.value); setResetError(""); }}
                        placeholder="your@email.com"
                        sx={{ ...fsx(), mb: resetError ? 1 : 2.5 }}
                      />

                      {resetError && (
                        <Typography sx={{ fontSize:11, color:"#888", fontFamily:"'Montserrat', sans-serif", mb:2, letterSpacing:"0.02em" }}>
                          {resetError}
                        </Typography>
                      )}

                      <Button
                        fullWidth
                        onClick={handleSendReset}
                        disabled={resetStatus === "sending"}
                        sx={{ py:1.6, borderRadius:0, background:"#ffffff", color:"#000000", fontFamily:"'Montserrat', sans-serif", fontSize:12, letterSpacing:"0.2em", fontWeight:500, "&:hover":{ background:"#e8e8e8" }, "&:disabled":{ background:"#222", color:"#555" } }}
                      >
                        {resetStatus === "sending" ? "SENDING..." : "SEND RESET LINK"}
                      </Button>
                    </motion.div>
                  )}

                </AnimatePresence>
              </motion.div>
            )}

            {/* ══════════════════════════════════════
                MAIN LOGIN / SIGNUP FORM
            ══════════════════════════════════════ */}
            {!showReset && (
              <motion.div
                key="main"
                initial={{ opacity:0, x:-24 }}
                animate={{ opacity:1, x:0 }}
                exit={{ opacity:0, x:-24 }}
                transition={{ duration:0.22, ease:"easeOut" }}
              >
                {/* Toggle */}
                <Box display="flex" justifyContent="center" mb={4}>
                  <div style={{ width:280, height:52, display:"flex", alignItems:"center", position:"relative", cursor:"pointer", border:"1px solid #2a2a2a", overflow:"hidden" }}>
                    <motion.div
                      key={mode}
                      initial={{ x: mode === "login" ? "-100%" : "100%" }}
                      animate={{ x:0 }}
                      transition={{ type:"spring", stiffness:280, damping:24 }}
                      style={{ position:"absolute", top:0, left: mode === "login" ? 0 : "50%", width:"50%", height:"100%", background:"#ffffff" }}
                    />
                    {(["login","signup"] as const).map(m => (
                      <div
                        key={m} onClick={() => handleToggle(m)}
                        style={{ flex:1, textAlign:"center", fontWeight:500, fontSize:12, letterSpacing:"0.2em", color: mode === m ? "#000000" : "#6b6b6b", zIndex:2, userSelect:"none", fontFamily:"'Montserrat', sans-serif", transition:"color 0.3s" }}
                      >
                        {m.toUpperCase()}
                      </div>
                    ))}
                  </div>
                </Box>

                {/* Email */}
                <TextField label="Email" fullWidth value={email} onChange={e => setEmail(e.target.value)} sx={fsx()}/>

                {/* Password */}
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth value={password}
                  onChange={e => setPassword(e.target.value)}
                  error={mode === "signup" && password.length > 0 && !allValid}
                  helperText={mode === "signup" && password ? `Strength: ${strengthLabel}` : ""}
                  sx={{ ...fsx(), mb: mode === "signup" ? 1 : 3 }}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color:"#6b6b6b" }}>
                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                      </IconButton>
                    </InputAdornment>
                  )}}
                />

                {/* Password checklist */}
                {mode === "signup" && password && (
                  <Box sx={{ mb:2, fontSize:11, color:"#6b6b6b", fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.03em" }}>
                    <div style={{ marginBottom:6, letterSpacing:"0.12em", fontSize:10, color:"#555" }}>REQUIREMENTS</div>
                    <ul style={{ margin:0, padding:"0 0 0 16px", listStyle:"none" }}>
                      {[
                        { ok: check.hasUpper,        text: "At least 1 uppercase letter" },
                        { ok: check.hasLower,         text: "At least 1 lowercase letter" },
                        { ok: check.hasNumber,        text: "At least 1 number" },
                        { ok: check.hasSpecial,       text: "At least 1 special character" },
                        { ok: check.validLength,      text: "6–12 characters long" },
                        { ok: check.notEmail,         text: "Not your email" },
                        { ok: check.notContainEmail,  text: "Doesn't contain email/username" },
                      ].map((item, i) => (
                        <li key={i} style={{ color: item.ok ? "#ffffff" : "#555555", padding:"3px 0", fontSize:11, display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ width:6, height:6, borderRadius:0, background: item.ok ? "#ffffff" : "#333333", border:`1px solid ${item.ok?"#ffffff":"#555555"}`, display:"inline-block", flexShrink:0 }}/>
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}

                {/* Confirm password */}
                {mode === "signup" && (
                  <TextField
                    label="Confirm Password" type="password" fullWidth
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    error={confirmPassword.length > 0 && confirmPassword !== password}
                    sx={{ ...fsx(), mb:3 }}
                  />
                )}

                {/* Error */}
                {error && (
                  <Typography sx={{ fontSize:12, textAlign:"center", mb:2, color:"#888888", fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.03em" }}>
                    {error}
                  </Typography>
                )}

                {/* CTA */}
                <Button
                  fullWidth
                  onClick={mode === "login" ? handleLogin : handleRegister}
                  disabled={status === "loading"}
                  sx={{ py:1.6, fontWeight:500, borderRadius:0, background:"#ffffff", color:"#000000", fontFamily:"'Montserrat', sans-serif", fontSize:12, letterSpacing:"0.2em", "&:hover":{ background:"#e8e8e8" }, "&:disabled":{ background:"#333333", color:"#6b6b6b" }, mb:2 }}
                >
                  {status === "loading"
                    ? mode === "login" ? "SIGNING IN..." : "REGISTERING..."
                    : mode === "login" ? "LOGIN" : "SIGNUP"}
                </Button>

                {/* Divider */}
                <Box sx={{ display:"flex", alignItems:"center", gap:2, mb:2 }}>
                  <Box sx={{ flex:1, height:"1px", background:"#1c1c1c" }}/>
                  <Typography sx={{ fontSize:10, color:"#555555", letterSpacing:"0.15em", fontFamily:"'Montserrat', sans-serif" }}>OR</Typography>
                  <Box sx={{ flex:1, height:"1px", background:"#1c1c1c" }}/>
                </Box>

                {/* Google */}
                <Button
                  fullWidth onClick={handleGoogle} variant="outlined"
                  startIcon={<Google sx={{ fontSize:16 }}/>}
                  sx={{ py:1.5, fontWeight:500, borderRadius:0, borderColor:"#2a2a2a", color:"#6b6b6b", fontFamily:"'Montserrat', sans-serif", fontSize:11, letterSpacing:"0.1em", "&:hover":{ background:"rgba(255,255,255,0.04)", borderColor:"#ffffff", color:"#ffffff" } }}
                >
                  Continue with Google
                </Button>

                {/* Footer */}
                {mode === "login" && (
                  <Typography sx={{ textAlign:"center", fontSize:12, mt:3, fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.03em" }}>
                    {/* ── Forgot password — underlined, opens inline reset panel ── */}
                    <span
                      onClick={openReset}
                      style={{ cursor:"pointer", color:"#6b6b6b", borderBottom:"1px solid #2a2a2a", paddingBottom:2, transition:"color 0.2s, border-color 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.color="#ffffff"; e.currentTarget.style.borderBottomColor="#ffffff"; }}
                      onMouseLeave={e => { e.currentTarget.style.color="#6b6b6b"; e.currentTarget.style.borderBottomColor="#2a2a2a"; }}
                    >
                      Forgot password?
                    </span>
                  </Typography>
                )}

                {mode === "signup" && (
                  <Typography sx={{ textAlign:"center", fontSize:12, mt:3, color:"#6b6b6b", fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.03em" }}>
                    Have an account?{" "}
                    <span style={{ cursor:"pointer", color:"#ffffff", borderBottom:"1px solid #2a2a2a", paddingBottom:2 }} onClick={() => handleToggle("login")}>
                      Login
                    </span>
                  </Typography>
                )}

              </motion.div>
            )}

          </AnimatePresence>
        </Box>

        {/* Explore button — outside the card, navigates to /accommodation */}
        <button className="explore-btn" onClick={() => navigate("/accommodation")}>
          <ExploreOutlined sx={{ fontSize: 16 }} />
          EXPLORE
        </button>

      </Box>
    </Box>
  );
};

export default AuthSwitch;